<template>
  <div class="compose">
    <h2 class="mr-4 h3">Skriv nyhedsbrev</h2>
    <b-row class="mb-3 mt-4">
      <b-col cols="6">
        <b-alert class="mt-3 mb-3" style="white-space: break-spaces;" :show="templateError !== null" variant="danger">
          {{templateError}}
        </b-alert>
        <b-form-group
          id="newsletter-subject"
          label="Emne"
          label-for="newsletter-subject-input"
          description="Emne for nyhedsbrevet"
        >
          <b-form-input
            id="newsletter-subject-input"
            v-model="newsletter.subject"
            placeholder=""
            :disabled="loadingTemplate"
          ></b-form-input>
        </b-form-group>
        <div class="mb-3">
          <label for="content">Indhold</label>
          <b-overlay :show="loadingTemplate" variant="dark" :opacity="0.15" spinner-variant="primary">
            <editor
              id="content"
              apiKey="bvtv0k3l32ywgtrypzpkaf1lcxq8lhg4uj4jjez261xz21jj"
              initialValue=""
              v-model="newsletter.content"
              :init="{
                height: 500,
                menubar: false,
                theme: 'silver',
                skin_url: '/oxide',
                branding: false,
                invalid_elements: 'div',
                plugins: [
                  'advlist autolink lists link image charmap',
                  'searchreplace visualblocks code fullscreen',
                  'print preview anchor insertdatetime media',
                  'paste code help wordcount table',
                  'visualblocks'
                ],
                toolbar:
                  'undo redo visualblocks | formatselect | bold italic | \
                  alignleft aligncenter alignright alignjustify | \
                  bullist numlist outdent indent | help'
              }"
            ></editor>
          </b-overlay>
        </div>
        <b-table
          class="mt-4"
          small
          :items="items"
          :fields="fields"
        >
          <template v-slot:cell(macro)="row">
            <span class="text-secondary">{{ row.item.macro }}</span>
          </template>
        </b-table>
        <b-form-group
          id="newsletter-date"
          label="Afgørelsesdato"
          label-for="newsletter-date-input"
          description="Afgørelser oprettet efter denne dato kommer med i nyhedsbrevet. Den bliver automatisk sat til den nuværende dato når et nyhedsbrev sendes"
        >
          <b-form-datepicker
            id="newsletter-date-input"
            locale="da"
            placeholder=""
            :labels="datePickerLabels"
            size="sm"
            v-model="date"
            today-variant="secondary"
            nav-button-variant="primary"
          >
            <template #button-content><b-icon icon="calendar">
              </b-icon><span class="hidden-text">Vælg en dato</span>
            </template>
          </b-form-datepicker>
        </b-form-group>
        <b-alert class="mt-3 mb-3" style="white-space: break-spaces;" :show="cutoffError !== null" variant="danger">
          {{cutoffError}}
        </b-alert>
        <hr>
        <b-form-group
          id="newsletter-test-recipients"
          label="Test modtagere"
          label-for="newsletter-test-recipients-input"
          description="Indtast en eller flere kommaseparerede emailadresser til modtagelse af testnyhedsbrev. Bemærk at når der afsendes et testnyhedsbrev ændres afgørelsesdatoen ikke."
        >
          <b-form-input
            id="newsletter-test-recipients-input"
            v-model="testRecipients"
            placeholder=""
          ></b-form-input>
        </b-form-group>
        <b-row>
          <b-col cols="12" md="6">
            <b-overlay :show="testing" variant="light" :opacity="0.5" spinner-variant="primary" spinner-small rounded="sm">   
              <b-button v-b-modal.modal-send-test class="w-100" variant="light" :disabled="sending || testing || testRecipients === ''">Send testnyhedsbrev</b-button>
            </b-overlay>
            <b-modal id="modal-send-test" title="Send testnyhedsbrev" cancel-title="Annullér" ok-title="Ja" cancel-variant="light" @ok="sendTest()">
              <p class="my-4">Vil du sende testnyhedsbrevet?</p>
            </b-modal>
          </b-col>
          <b-col cols="12" md="6">
            <b-overlay :show="sending" variant="light" :opacity="0.5" spinner-variant="primary" spinner-small rounded="sm">   
              <b-button v-b-modal.modal-send class="w-100" variant="primary" :disbaled="sending || testing">Send nyhedsbrev</b-button>
            </b-overlay>
            <b-modal id="modal-send" title="Send nyhedsbrev" cancel-title="Annullér" ok-title="Ja" cancel-variant="light" @ok="send()">
              <p class="my-4">Vil du sende nyhedsbrevet?</p>
            </b-modal>
            
          </b-col>
          <b-col cols="12">
            <b-alert class="mt-3 mb-3" style="white-space: break-spaces;" :show="sendAlert !== null" :variant="sendAlertVariant">
              {{sendAlert}}
            </b-alert>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="6">
        <h2 class="h6">Forhåndsvisning</h2>
        <b-alert class="mt-3 mb-3" style="white-space: break-spaces;" :show="previewError !== null" variant="danger">
          {{previewError}}
        </b-alert>
        <b-card>
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <span>Emne: {{previewSubject}}</span>
              <b-button @click="getPreview" size="sm" variant="primary" :disabled="loadingPreview"><b-icon icon="arrow-counterclockwise"></b-icon></b-button>
            </div>
          </template>
          <b-overlay :show="loadingPreview" variant="dark" :opacity="0.15" spinner-variant="primary">
            <div v-if="loadingPreview" style="height:500px;"></div>
            <div v-else v-html="preview"></div>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import tinymce from 'tinymce/tinymce'
import 'tinymce/themes/silver/theme'
import 'tinymce/icons/default'
import 'tinymce/plugins/advlist'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/image'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/searchreplace'
import 'tinymce/plugins/visualblocks'
import 'tinymce/plugins/code'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/print'
import 'tinymce/plugins/preview'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/insertdatetime'
import 'tinymce/plugins/media'
import 'tinymce/plugins/paste'
import 'tinymce/plugins/code'
import 'tinymce/plugins/help'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/table'
import 'tinymce/plugins/visualblocks'
export default {
  components: {
    Editor
  },
  data: () => ({
    sending: false,
    testing: false,
    sendAlert: null,
    sendAlertVariant: 'danger',
    testRecipients: '',
    newsletter: {
      subject: '',
      content: ''
    },
    template: null,
    loadingTemplate: true,
    templateError: null,
    date: null,
    cutoffError: null,
    loadingCutoff: true,
    previewSubject: null,
    preview: null,
    loadingPreview: true,
    previewError: null,
    datePickerLabels: {
      labelPrevDecade: 'Forrige årti',
      labelPrevYear: 'Forrige år',
      labelPrevMonth: 'Forrige måned',
      labelCurrentMonth: 'Nuværende måned',
      labelNextMonth: 'Næste måned',
      labelNextYear: 'Næste år',
      labelNextDecade: 'Næste årti',
      labelToday: 'Idag',
      labelSelected: 'Valgt dato',
      labelNoDateSelected: 'Ingen dato valgt',
      labelCalendar: 'Kalender',
      labelNav: 'Kalendernavigation',
      labelHelp: 'Brug piletasterne til at navigere med'
    },
    fields: [
      {
        key: 'macro',
        label: 'Macro'
      },
      {
        key: 'description',
        label: 'Beskrivelse'
      }
    ]
  }),
  mounted() {
    this.axios.get('/api/newsletters/templates/newsletter', this.config).then((response) => {
      this.template = response.data.data
      this.newsletter.subject = response.data.data.summary_template
      this.newsletter.content = response.data.data.body_template
    }).then(() => {
      this.getCutoffDate()
    }).catch(error => {
      this.templateError = 'Nyhedsbrevsskabelonen kunne ikke hentes\n\n' + error + '\n\nPrøv igen senere'
    })
    tinymce.init({
      invalid_elements : 'div'
    })
  },
  computed: {
    config() {
      return this.$store.getters.getConfig
    },
    items() {
      return this.$store.state.macros
    }
  },
  methods: {
    getCutoffDate() {
      this.cutoffError = null
      this.loadingCutoff = true
      this.axios.get('/api/newsletters/cutoff_date', this.config).then((response) => {
        this.loadingCutoff = false
        this.date = this.$options.filters.moment(response.data.data.cutoff_date, 'YYYY-MM-DD')
      }).then(() => {
        this.loadingTemplate = false
        this.getPreview()
      }).catch(error => {
        this.loadingCutoff = false
        this.cutoffError = 'Afgørelsesdato kunne ikke hentes\n\n' + error + '\n\nPrøv igen senere'
      })
    },
    getPreview() {
      this.previewError = null
      this.loadingPreview = true
      this.axios.post('/api/newsletters/actions/preview', {
        subject: this.newsletter.subject,
        body: this.newsletter.content,
        cutoff_date: this.date
      }, this.config).then((response) => {
        this.loadingPreview = false
        this.preview = response.data.body
        this.previewSubject = response.data.subject
      }).catch(error => {
        this.loadingPreview = false
        this.previewError = 'Forhåndsvisningen kunne ikke hentes\n\n' + error + '\n\nPrøv igen senere'
      })
    },
    send() {
      this.sending = true
      this.sendAlert = null
      this.axios.post('/api/newsletters/actions/send', {
        subject: this.newsletter.subject,
        body: this.newsletter.content,
        cutoff_date: this.date,
      }, this.config).then(() => {
        this.sending = false
        this.sendAlert = 'Nyhedsbrevet er blevet sendt'
        this.sendAlertVariant = 'success'
      }).catch(error => {
        this.sending = false
        this.sendAlert = 'Nyhedsbrevet kunne ikke afsendes\n\n' + error + '\n\nPrøv igen senere'
        this.sendAlertVariant = 'danger'
      })
    },
    sendTest() {
      this.testing = true
      this.sendAlert = null
      this.axios.post('/api/newsletters/actions/send/test', {
        subject: this.newsletter.subject,
        body: this.newsletter.content,
        cutoff_date: this.date,
        recipients: this.testRecipients
      }, this.config).then(() => {
        this.testing = false
        this.sendAlert = 'Nyhedsbrevet er blevet sendt'
        this.sendAlertVariant = 'success'
      }).catch(error => {
        this.testing = false
        this.sendAlert = 'Nyhedsbrevet kunne ikke testafsendes\n\n' + error + '\n\nPrøv igen senere'
        this.sendAlertVariant = 'danger'
      })
    }
  }
}
</script>

<style>

</style>